import React, { useState, useEffect, useCallback, useRef } from "react";
import { Phone, PhoneCall, Loader2, PhoneOutgoing, Clock, FileText, Play, RefreshCw, Sparkles, Upload, Search, User, Users, UserPlus, Send, X, Megaphone } from "lucide-react";
import { phoneCall, phoneCallDetails, phoneCalls, contactsImport, contactsList, phoneRelay } from "../lib/api";
import { toast } from "sonner";

const VOICES = [
  { id: "tanner-british", label: "Tanner — British Male (Nexus)" },
  { id: "ryan", label: "Ryan — Professional US Male" },
  { id: "david", label: "David — US Male" },
  { id: "ravi", label: "Ravi — Indian Male" },
  { id: "josh", label: "Josh — Articulate US Male" },
  { id: "adriana", label: "Adriana — Professional US Female" },
  { id: "maya", label: "Maya — Young US Female" },
];

const DONE = ["complete", "completed", "failed", "error", "no-answer", "busy", "canceled"];

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const done = s === "complete" || s === "completed";
  const bad = ["failed", "error", "no-answer", "busy", "canceled"].includes(s);
  const color = done ? "#10B981" : bad ? "#EF4444" : "#00F0FF";
  const label = done ? "Done" : bad ? s : (s === "queued" || s === "" ? "Dialing…" : s === "started" || s === "in-progress" ? "On the call…" : s);
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
      style={{ color, background: `${color}18`, border: `1px solid ${color}44` }} data-testid="phone-status-pill">
      {!done && !bad && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: color }} />}
      {label}
    </span>
  );
}

function CallCard({ call, userId, live, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const poll = useCallback(async () => {
    if (!call.call_id) return;
    try {
      const d = await phoneCallDetails(call.call_id, userId);
      onUpdate({ ...call, ...d });
      return d.status;
    } catch (e) { /* keep trying */ }
  }, [call, userId, onUpdate]);

  useEffect(() => {
    if (!live) return;
    if (DONE.includes((call.status || "").toLowerCase())) return;
    const t = setInterval(poll, 4000);
    poll();
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, call.call_id, call.status]);

  const refresh = async () => { setBusy(true); await poll(); setBusy(false); };

  return (
    <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4" data-testid={`phone-call-${call.call_id || call.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <PhoneOutgoing className="w-4 h-4 text-[#00F0FF] shrink-0" />
            <p className="text-cyan-50 font-bold truncate">{call.callee || call.to}</p>
          </div>
          {call.callee ? <p className="text-cyan-200/40 text-[11px] mt-0.5">{call.to}</p> : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusPill status={call.status} />
          <button onClick={refresh} disabled={busy} className="p-1.5 rounded-lg text-cyan-200/50 hover:text-cyan-100 hover:bg-[#00F0FF11]" data-testid="phone-refresh-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      <p className="text-cyan-100/70 text-xs mt-2"><span className="text-cyan-200/40">Task:</span> {call.objective}</p>

      {call.call_length ? (
        <p className="text-cyan-200/40 text-[11px] mt-1.5 flex items-center gap-1"><Clock className="w-3 h-3" /> {Math.round(call.call_length * 60) / 60} min{call.answered_by ? ` · answered by ${call.answered_by}` : ""}</p>
      ) : null}

      {call.summary ? (
        <div className="mt-3 bg-[#00F0FF08] border border-[#00F0FF22] rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-widest text-[#00F0FF] mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> What happened</p>
          <p className="text-cyan-50 text-sm whitespace-pre-wrap" data-testid="phone-call-summary">{call.summary}</p>
        </div>
      ) : null}

      {call.error_message ? <p className="text-rose-300 text-xs mt-2">{call.error_message}</p> : null}

      {call.recording_url ? (
        <a href={call.recording_url} target="_blank" rel="noreferrer" data-testid="phone-recording-link"
          className="inline-flex items-center gap-1.5 mt-3 text-[#00F0FF] text-xs font-bold hover:underline"><Play className="w-3.5 h-3.5" /> Listen to recording</a>
      ) : null}

      {call.transcript ? (
        <details className="mt-2">
          <summary className="text-cyan-200/50 text-xs cursor-pointer hover:text-cyan-100 flex items-center gap-1"><FileText className="w-3 h-3" /> Full transcript</summary>
          <pre className="text-cyan-100/70 text-[11px] mt-2 whitespace-pre-wrap font-sans max-h-64 overflow-y-auto">{call.transcript}</pre>
        </details>
      ) : null}
    </div>
  );
}

function RelayCard({ userId, onSent }) {
  const [recipients, setRecipients] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [manualName, setManualName] = useState("");
  const [manualPhone, setManualPhone] = useState("");
  const [message, setMessage] = useState("");
  const [voice, setVoice] = useState("tanner-british");
  const [sending, setSending] = useState(false);

  const search = async (val) => {
    setQ(val);
    if (val.trim().length >= 1) { try { const r = await contactsList(userId, val.trim()); setResults(r.contacts || []); } catch (e) { /* noop */ } }
    else setResults([]);
  };
  const add = (name, phone) => {
    const p = (phone || "").replace(/[^\d+]/g, "");
    if (!p) return;
    if (recipients.some((r) => r.phone === p)) { toast.info("Already added"); return; }
    setRecipients((prev) => [...prev, { name: name || p, phone: p }]);
    setQ(""); setResults([]); setManualName(""); setManualPhone("");
  };
  const remove = (p) => setRecipients((prev) => prev.filter((r) => r.phone !== p));

  const deliver = async () => {
    if (recipients.length === 0) { toast.error("Add at least one person"); return; }
    if (!message.trim()) { toast.error("Type the message Nexus should deliver"); return; }
    setSending(true);
    try {
      const r = await phoneRelay(userId, recipients, message, voice);
      const failed = (r.results || []).filter((x) => !x.ok);
      toast.success(`Nexus is calling ${r.sent} ${r.sent === 1 ? "person" : "people"}…`);
      if (failed.length) toast.error(`${failed.length} could not be dialed (check the number)`);
      setRecipients([]); setMessage("");
      onSent && onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start the relay"); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-[#0b0716] border border-[#a855f733] rounded-2xl p-4 space-y-3 mt-4" data-testid="phone-relay-card">
      <div className="flex items-center gap-2">
        <Megaphone className="w-5 h-5 text-[#c084fc]" />
        <h3 className="text-[#c084fc] font-bold">Relay to your team</h3>
      </div>
      <p className="text-purple-200/50 text-xs -mt-1">Nexus calls each person and delivers your message in your words, then reports who confirmed.</p>

      <div className="relative">
        <Search className="w-4 h-4 text-purple-200/40 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => search(e.target.value)} data-testid="relay-contact-search" placeholder="Search contacts to add…"
          className="w-full bg-[#140b22] border border-[#a855f733] rounded-xl pl-9 pr-3 py-2.5 text-purple-50 placeholder:text-purple-200/25 focus:outline-none focus:border-[#c084fc]" />
        {results.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 max-h-52 overflow-y-auto bg-[#140b22] border border-[#a855f755] rounded-xl shadow-xl">
            {results.map((c) => (
              <button key={c.id} onClick={() => add(c.name, c.phone)} data-testid={`relay-add-${c.id}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#a855f722] border-b border-[#a855f711] last:border-0">
                <User className="w-3.5 h-3.5 text-[#c084fc] shrink-0" />
                <span className="text-purple-50 text-sm truncate flex-1">{c.name}</span>
                <span className="text-purple-200/40 text-xs">{c.phone}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <input value={manualName} onChange={(e) => setManualName(e.target.value)} data-testid="relay-manual-name" placeholder="Name"
          className="flex-1 bg-[#140b22] border border-[#a855f733] rounded-xl px-3 py-2 text-purple-50 text-sm placeholder:text-purple-200/25 focus:outline-none focus:border-[#c084fc]" />
        <input value={manualPhone} onChange={(e) => setManualPhone(e.target.value)} data-testid="relay-manual-phone" placeholder="+1 312 555 1234"
          className="flex-1 bg-[#140b22] border border-[#a855f733] rounded-xl px-3 py-2 text-purple-50 text-sm placeholder:text-purple-200/25 focus:outline-none focus:border-[#c084fc]" />
        <button onClick={() => add(manualName, manualPhone)} data-testid="relay-manual-add"
          className="px-3 rounded-xl text-[#c084fc] border border-[#a855f744] hover:bg-[#a855f722]"><UserPlus className="w-4 h-4" /></button>
      </div>

      {recipients.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="relay-recipients">
          {recipients.map((r) => (
            <span key={r.phone} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-[#a855f722] border border-[#a855f744] text-purple-50 text-xs">
              {r.name}
              <button onClick={() => remove(r.phone)} data-testid={`relay-remove-${r.phone}`} className="p-0.5 rounded-full hover:bg-[#a855f744]"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}

      <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="relay-message-input" rows={3}
        placeholder="e.g. Reminder from Sohel: please open the store by 8 AM sharp tomorrow and count the register before you start."
        className="w-full bg-[#140b22] border border-[#a855f733] rounded-xl px-3 py-2.5 text-purple-50 placeholder:text-purple-200/25 focus:outline-none focus:border-[#c084fc] resize-none" />

      <div className="flex items-center gap-3">
        <select value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="relay-voice-select"
          className="bg-[#140b22] border border-[#a855f733] rounded-xl px-3 py-2.5 text-purple-50 text-sm focus:outline-none focus:border-[#c084fc]">
          {VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
        </select>
        <button onClick={deliver} disabled={sending} data-testid="relay-deliver-btn"
          className="flex-1 py-3 rounded-full text-sm font-bold text-[#0b0716] bg-[#c084fc] hover:bg-[#d8b4fe] disabled:opacity-50 flex items-center justify-center gap-2">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Nexus, deliver this
        </button>
      </div>
    </div>
  );
}

export default function NexusPhone({ session }) {
  const userId = session.user_id;
  const [fromNumber, setFromNumber] = useState("");
  const [to, setTo] = useState("+1");
  const [callee, setCallee] = useState("");
  const [objective, setObjective] = useState("");
  const [voice, setVoice] = useState("tanner-british");
  const [placing, setPlacing] = useState(false);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const liveIdRef = useRef(null);
  const [contactTotal, setContactTotal] = useState(0);
  const [cq, setCq] = useState("");
  const [cResults, setCResults] = useState([]);
  const [importing, setImporting] = useState(false);
  const contactFileRef = useRef(null);

  const loadContacts = useCallback(async (q = "") => {
    try {
      const r = await contactsList(userId, q);
      setContactTotal(r.total || 0);
      setCResults(q ? (r.contacts || []) : []);
    } catch (e) { /* noop */ }
  }, [userId]);
  useEffect(() => { loadContacts(""); }, [loadContacts]);

  const onImportContacts = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const r = await contactsImport(userId, file);
      toast.success(`Imported ${r.imported} new contact${r.imported === 1 ? "" : "s"} — ${r.total} total`);
      loadContacts("");
    } catch (err) {
      toast.error("Could not import — export your contacts as a .vcf or .csv file");
    } finally {
      setImporting(false);
      if (contactFileRef.current) contactFileRef.current.value = "";
    }
  };

  const onContactSearch = (val) => {
    setCq(val);
    if (val.trim().length >= 1) loadContacts(val.trim());
    else setCResults([]);
  };
  const pickContact = (c) => { setTo(c.phone); setCallee(c.name); setCq(""); setCResults([]); };

  const load = useCallback(async () => {
    try {
      const r = await phoneCalls(userId);
      setCalls(r.calls || []);
      setFromNumber(r.from_number || "");
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const place = async () => {
    if (!/^\+[1-9]\d{7,14}$/.test(to.replace(/[^\d+]/g, ""))) { toast.error("Enter a full number with country code, e.g. +13125551234"); return; }
    if (!objective.trim()) { toast.error("Tell Nexus what the call is about"); return; }
    setPlacing(true);
    try {
      const r = await phoneCall(userId, { to, objective, callee, voice });
      toast.success("Nexus is dialing…");
      liveIdRef.current = r.call_id;
      setObjective(""); setCallee("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place the call");
    } finally { setPlacing(false); }
  };

  const updateCall = (updated) => {
    setCalls((prev) => prev.map((c) => (c.call_id === updated.call_id ? { ...c, ...updated } : c)));
  };

  return (
    <div className="h-full overflow-y-auto bg-[#030712] nx-display" data-testid="nexus-phone-panel">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="mb-5">
          <h2 className="text-[#00F0FF] font-bold text-xl flex items-center gap-2"><Phone className="w-6 h-6" /> Nexus Phone</h2>
          <p className="text-cyan-200/50 text-sm mt-1">
            Nexus makes real calls for you — book appointments, call stores, ask questions — then reports back.
          </p>
          {fromNumber ? (
            <p className="text-cyan-100 text-sm mt-2 inline-flex items-center gap-2 bg-[#00F0FF11] border border-[#00F0FF33] rounded-full px-3 py-1.5">
              <PhoneCall className="w-3.5 h-3.5 text-[#00F0FF]" /> Nexus's number: <span className="font-bold" data-testid="phone-from-number">{fromNumber}</span>
            </p>
          ) : null}
        </div>

        <a href="tel:+17738392574" data-testid="call-nexus-card"
           className="block mb-5 rounded-2xl p-4 bg-gradient-to-br from-[#00F0FF15] to-[#0a1020] border border-[#00F0FF44] hover:border-[#00F0FF] transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#00F0FF] text-[#030712] flex items-center justify-center shrink-0">
              <PhoneCall className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-cyan-50 font-bold text-sm">Call Nexus directly — 2-way live</p>
              <p className="text-cyan-200/70 text-xs">Tap to talk to Nexus on the phone anytime: <span className="font-bold text-[#00F0FF]">+1 773 839 2574</span></p>
            </div>
          </div>
        </a>


        <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-cyan-200/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={cq} onChange={(e) => onContactSearch(e.target.value)} data-testid="phone-contact-search"
                placeholder={contactTotal > 0 ? `Search your ${contactTotal} contacts…` : "Search contacts (import first)"}
                className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl pl-9 pr-3 py-2.5 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66]" />
              {cResults.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-[#0a1020] border border-[#00F0FF44] rounded-xl shadow-xl" data-testid="phone-contact-results">
                  {cResults.map((c) => (
                    <button key={c.id} onClick={() => pickContact(c)} data-testid={`phone-contact-${c.id}`}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#00F0FF11] border-b border-[#00F0FF11] last:border-0">
                      <User className="w-3.5 h-3.5 text-[#00F0FF] shrink-0" />
                      <span className="text-cyan-50 text-sm truncate flex-1">{c.name}</span>
                      <span className="text-cyan-200/40 text-xs">{c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input ref={contactFileRef} type="file" accept=".vcf,.csv,text/vcard,text/csv" className="hidden" onChange={onImportContacts} data-testid="phone-contacts-file" />
            <button onClick={() => contactFileRef.current?.click()} disabled={importing} data-testid="phone-import-contacts-btn"
              className="px-3 py-2.5 rounded-xl text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1.5 shrink-0 disabled:opacity-50">
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Import
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-cyan-200/50">Number to call</label>
              <input value={to} onChange={(e) => setTo(e.target.value)} data-testid="phone-to-input" placeholder="+1 312 555 1234"
                className="w-full mt-1 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66]" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-cyan-200/50">Who is it? (optional)</label>
              <input value={callee} onChange={(e) => setCallee(e.target.value)} data-testid="phone-callee-input" placeholder="e.g. Luigi's Pizzeria"
                className="w-full mt-1 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66]" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-cyan-200/50">What should Nexus do on the call?</label>
            <textarea value={objective} onChange={(e) => setObjective(e.target.value)} data-testid="phone-objective-input" rows={3}
              placeholder="Book a table for 2 tomorrow at 7 PM under the name Sohel. Ask if they have outdoor seating."
              className="w-full mt-1 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66] resize-none" />
          </div>
          <div className="flex items-center gap-3">
            <select value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="phone-voice-select"
              className="bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 text-sm focus:outline-none focus:border-[#00F0FF66]">
              {VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
            <button onClick={place} disabled={placing} data-testid="phone-place-call-btn"
              className="flex-1 py-3 rounded-full text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-[#5ff7ff] disabled:opacity-50 flex items-center justify-center gap-2">
              {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />} Nexus, make the call
            </button>
          </div>
        </div>

        <RelayCard userId={userId} onSent={load} />

        <div className="mt-6">
          <h3 className="text-cyan-200/60 text-xs uppercase tracking-widest mb-3">Call history</h3>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-cyan-200/40"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : calls.length === 0 ? (
            <p className="text-cyan-200/40 text-sm text-center py-8" data-testid="phone-empty">No calls yet. Ask Nexus to make one above.</p>
          ) : (
            <div className="space-y-3" data-testid="phone-call-list">
              {calls.map((c) => (
                <CallCard key={c.call_id || c.id} call={c} userId={userId}
                  live={!DONE.includes((c.status || "").toLowerCase())} onUpdate={updateCall} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
